import React, { useEffect, useRef, forwardRef } from 'react';

interface AutoFocusTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  focusDelay?: number; // Optional delay before focusing (useful for animations)
}

// eslint-disable-next-line react/prop-types
const AutoFocusTextarea = forwardRef<HTMLTextAreaElement, AutoFocusTextareaProps>((props, ref) => {
  const { disabled, focusDelay = 0, ...rest } = props;
  const localRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || localRef;
  const wasDisabled = useRef<boolean | undefined>(disabled);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    // Focus logic
    const focusTextarea = (label: string) => {
      if (textareaRef.current && !disabled) {
        textareaRef.current.focus();
        textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        // Debug log
        // eslint-disable-next-line no-console
        console.log(`[AutoFocusTextarea] Focus attempt (${label}):`, textareaRef.current, 'isFocused:', document.activeElement === textareaRef.current);
      }
    };

    if (!disabled) {
      focusTimeoutRef.current = setTimeout(() => {
        focusTextarea('initial');
        // If not focused, try again after a short delay
        setTimeout(() => {
          focusTextarea('setTimeout');
          // Try again on next animation frame
          requestAnimationFrame(() => {
            focusTextarea('rAF');
          });
        }, 100);
      }, focusDelay);
    }

    if (wasDisabled.current && !disabled) {
      focusTimeoutRef.current = setTimeout(() => {
        focusTextarea('re-enabled');
        setTimeout(() => {
          focusTextarea('re-enabled setTimeout');
          requestAnimationFrame(() => {
            focusTextarea('re-enabled rAF');
          });
        }, 100);
      }, focusDelay);
    }

    wasDisabled.current = disabled;

    // Cleanup timeout on unmount
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, [disabled, focusDelay, textareaRef]);

  return <textarea ref={textareaRef} disabled={disabled} {...rest} />;
});

AutoFocusTextarea.displayName = 'AutoFocusTextarea';

export default AutoFocusTextarea; 